import React, { useRef, useState, useEffect } from 'react';
import './CommentArea.css';
import { MessageCircle, X } from 'lucide-react';
// 02.9-shared-widgets.md paso a (§0.4) -- excepción ya decidida: este archivo hace su propio
// trabajo de dominio (upload + insert en el contentEditable, mismo criterio que
// MailComposer.jsx); envolver una sola llamada en un hook solo para el linter sería
// sobre-ingeniería.
// eslint-disable-next-line no-restricted-imports -- excepción documentada (ver arriba). Fase 5.8.
import { uploadFile as uploadFileToGcs } from '../../../shared/api/uploadFile';
import { useAllUsers } from '../../../shared/hooks/useAllUsers';
import { useCreateComment } from '../hooks/useComments';
// 02.9-shared-widgets.md paso a (§0.5) -- excepción genuina: `DoctexHtml.jsx` y este archivo
// (montado aparte vía UniversalCommentModule <- FilesAndMedia/CosmicClient) no comparten
// ancestro en el árbol de React, así que no hay container real desde el cual threadear este
// estado. (README §1.2: `CommentDraftContext` es 1 de los 3 contextos que lee `ComponentTaskHoy`
// — cuando 5.17 haga el `TasksScreen` container, revisar si conviene threadear desde ahí.)
// eslint-disable-next-line no-restricted-imports -- excepción documentada (ver arriba). Fase 5.8.
import { useCommentDraft } from '../context/CommentDraftContext';
import { useScreenRecordStore } from '../../../shared/media/useScreenRecordStore';
import { KEYWORDS } from '../lib/commentKeywords';
import CommentMentionSuggestions from './CommentMentionSuggestions';
import CommentToolbar from './CommentToolbar';

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: limpia la lista de usuarios filtrados cuando el query de mención se vacía.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const CommentArea = ({ docId, user, toast }) => {
    const createComment = useCreateComment();
    const { pendingAnchor, clearPendingAnchor, setRealizedAnchor, setCancelledAnchorId } = useCommentDraft();
    const editorRef = useRef(null);
    const canvasRef = useRef(null);
    const [smartTags, setSmartTags] = useState([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isDrawingMouse, setIsDrawingMouse] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [dropdownCoords, setDropdownCoords] = useState({ left: 0, bottom: 0 }); // Added state for portal positioning
    const [isRecordingFromComment, setIsRecordingFromComment] = useState(false);

    // --- Feature: User Mentions (@tagging) ---
    // 02.9-shared-widgets.md paso a (§0.4) -- reemplaza el `apiClient.get('/api/users')` a mano
    // (import prohibido, §0.1) por el hook compartido, ya cacheado por React Query.
    const { data: allUsers = [] } = useAllUsers();
    const [mentionQuery, setMentionQuery] = useState(null);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [mentionIndex, setMentionIndex] = useState(0);

    // 2. Filter Users when Query Changes
    useEffect(() => {
        if (mentionQuery === null) {
            setFilteredUsers([]);
            return;
        }
        const lower = mentionQuery.toLowerCase();
        // Top 3 matches
        const matches = allUsers
            .filter(u => (u.nick || u.name || '').toLowerCase().includes(lower))
            .slice(0, 3);
        setFilteredUsers(matches);
        setMentionIndex(0); // Reset nav
    }, [mentionQuery, allUsers]);


    useEffect(() => {
        if (mentionQuery !== null && filteredUsers.length > 0 && editorRef.current) {
            const rect = editorRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            if (spaceBelow < 200 && spaceAbove > 200) {
                // Show above editor
                setDropdownCoords({
                    left: rect.left,
                    bottom: window.innerHeight - rect.top + 5,
                    top: 'auto'
                });
            } else {
                // Show below editor
                setDropdownCoords({
                    left: rect.left,
                    top: rect.bottom + 5,
                    bottom: 'auto'
                });
            }
        }
    }, [mentionQuery, filteredUsers.length]);

    const handleInput = () => {
        if (!editorRef.current) return;
        const textFull = editorRef.current.innerText.toLowerCase();

        // Existing Smart Tags Logic
        const foundTags = [];
        Object.keys(KEYWORDS).forEach(key => {
            if (textFull.includes(key)) foundTags.push(KEYWORDS[key]);
        });
        setSmartTags([...new Set(foundTags)]);

        // Mention Logic Detection
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const node = range.startContainer;
            let text = "";
            let offset = 0;
            
            if (node.nodeType === 3) {
                text = node.textContent;
                offset = range.startOffset;
            } else if (node.nodeType === 1) {
                text = node.textContent;
                offset = text.length;
            }

            if (text !== undefined && text !== null) {
                const before = text.slice(0, offset);
                // Sin exigir espacio/inicio antes del "@" -- si el usuario lo escribe pegado a
                // otra palabra, debe seguir abriendo el popup de menciones (mismo criterio que
                // useMentions.js/DoctexHtml.jsx, que ya no exigen ese prefijo).
                const match = before.match(/@(\S*)$/);
                if (match) {
                    setMentionQuery(match[1]);
                } else {
                    setMentionQuery(null);
                }
            } else {
                setMentionQuery(null);
            }
        }
    };

    // Screen recording finish — `useScreenRecordStore` bumps `finishedTick` when an upload
    // lands; act only if this editor is the one that started the recording.
    const recStartTickRef = useRef(0);
    const recFinishedTick = useScreenRecordStore((s) => s.finishedTick);
    useEffect(() => {
        if (!isRecordingFromComment || recFinishedTick <= recStartTickRef.current) return;
        const url = useScreenRecordStore.getState().lastUrl;
        setIsRecordingFromComment(false);
        if (!url) return;

        // create video tag to insert into editor
        const videoHtml = `<div><video playsinline controls src="${url}" style="max-width: 100%; max-height: 400px; border-radius: 8px; margin: 10px 0; background-color: #000; display: block;"></video></div><br/>`;
        if (editorRef.current) {
            editorRef.current.focus();
            try {
                const selection = window.getSelection();
                if (selection.rangeCount > 0) selection.collapseToEnd();
                document.execCommand('insertHTML', false, videoHtml);
            } catch (err) {
                console.error("ExecCommand error on insert video. Fallback applied", err);
                editorRef.current.innerHTML += videoHtml;
            }
        }
    }, [isRecordingFromComment, recFinishedTick]);

    const handleToggleRecording = () => {
        if (!isRecordingFromComment) {
            recStartTickRef.current = useScreenRecordStore.getState().finishedTick;
            setIsRecordingFromComment(true);
        }
        useScreenRecordStore.getState().toggle();
    };

    const insertMention = (user) => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        const range = selection.getRangeAt(0);
        const node = range.startContainer;

        if (node.nodeType === 3 || node.nodeType === 1) {
            let text = node.textContent;
            let offset = range.startOffset;
            if (node.nodeType === 1) offset = text.length;

            const before = text.slice(0, offset);
            const match = before.match(/@(\S*)$/);

            if (match) {
                const atIndex = before.lastIndexOf('@');
                const start = atIndex;
                const end = offset;

                if (node.nodeType === 3) {
                    // Set range to cover "@query"
                    range.setStart(node, start);
                    range.setEnd(node, end);
                    range.deleteContents();
                } else {
                    // Fallback for element node
                    node.textContent = node.textContent.slice(0, atIndex) + node.textContent.slice(end);
                    range.setStart(node, node.childNodes.length || 0);
                    range.collapse(true);
                }

                // Create Pill
                const pillSpan = document.createElement('span');
                pillSpan.className = 'mention-pill';
                pillSpan.contentEditable = "false";
                const avatarUrl = (user['link-profile-img-thumb'] || user['link-profile-img']) || user.image || `https://ui-avatars.com/api/?name=${user.nick}`;
                pillSpan.setAttribute('data-uid', user.id || user.dbId || '');
                pillSpan.setAttribute('data-name', user.nick || '');
                pillSpan.setAttribute('data-avatar', avatarUrl);
                // Add img + text
                pillSpan.innerHTML = `<img src="${avatarUrl}" /> ${user.nick}`;

                range.insertNode(pillSpan);

                // Add nbsp
                const space = document.createTextNode('\u00A0');
                range.setStartAfter(pillSpan);
                range.insertNode(space);

                // Move Cursor
                range.setStartAfter(space);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
        setMentionQuery(null);
        editorRef.current.focus();
    };

    const handleKeyDown = (e) => {
        // Intercept Navigation if Mention Menu is Open
        if (mentionQuery !== null && filteredUsers.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMentionIndex(prev => Math.min(prev + 1, filteredUsers.length - 1));
                return;
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMentionIndex(prev => Math.max(prev - 1, 0));
                return;
            } else if (e.key === 'Enter') {
                e.preventDefault();
                insertMention(filteredUsers[mentionIndex]);
                return;
            } else if (e.key === 'Escape') {
                setMentionQuery(null);
                return;
            } else if (e.key === 'Tab') {
                e.preventDefault();
                insertMention(filteredUsers[mentionIndex]);
                return;
            }
        }

        // Existing logic (none really, but preservation)
    };

    const execCmd = (cmd) => {
        document.execCommand(cmd, false, null);
        editorRef.current.focus();
    };

    const uploadFile = async (file) => {
        if (!file) return null;

        try {
            const url = await uploadFileToGcs(file, 'comment_embed', {
                extraFields: user?.id ? { userId: user.id } : {},
                onProgress: setUploadProgress,
            });

            if (editorRef.current) {
                editorRef.current.focus();

                const ext = file.name.split('.').pop().toLowerCase();
                const renderableImages = ['png', 'gif', 'webp', 'svg', 'avif', 'bmp', 'jpg', 'jpeg'];
                const renderableVideos = ['mp4', 'webm', 'mov', 'ogg', 'quicktime'];

                if (renderableImages.includes(ext)) {
                    const imgHtml = `<img src="${url}" alt="${file.name}" style="max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; display: block;" contenteditable="false" />&nbsp;`;
                    document.execCommand('insertHTML', false, imgHtml);
                } else if (renderableVideos.includes(ext)) {
                    const videoHtml = `<video src="${url}" controls style="max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; display: block;" contenteditable="false"></video>&nbsp;`;
                    document.execCommand('insertHTML', false, videoHtml);
                } else {
                    const svg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path></svg>`;
                    const html = `<a href="${url}" target="_blank" class="file-tag" contenteditable="false" style="text-decoration:none; cursor:pointer;">${svg} ${file.name}</a>&nbsp;`;
                    document.execCommand('insertHTML', false, html);
                }
            }
            setUploadProgress(0);
            return url;
        } catch (e) {
            toast.error('Upload Error: ' + (e.message || 'Unknown error'));
            setUploadProgress(0);
            throw e;
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            for (let i = 0; i < e.dataTransfer.files.length; i++) {
                await uploadFile(e.dataTransfer.files[i]);
            }
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handlePaste = (e) => {
        e.preventDefault();
        if (e.clipboardData.files && e.clipboardData.files.length > 0) {
            uploadFile(e.clipboardData.files[0]);
            return;
        }

        const text = e.clipboardData.getData('text/plain');
        if (text.match(/^(http|https):\/\/[^ "]+$/)) {
            const html = `<span class="link-tag" contenteditable="false">${text}</span>&nbsp;`;
            document.execCommand('insertHTML', false, html);
        } else {
            document.execCommand('insertText', false, text);
        }
    };

    const submitComment = async () => {
        if (!editorRef.current) return;
        
        const hasVisibleText = editorRef.current.innerText.trim().length > 0;
        const hasMedia = editorRef.current.querySelector('img, video, a');
        
        if (!hasVisibleText && !hasMedia) return;
        
        if (!user || !user.id) {
            toast.error("Error: Usuario no identificado. Recarga la página.");
            console.error("User not logged in");
            return;
        }
        if (!docId) {
            toast.error("Error: No se ha seleccionado ningún documento.");
            console.error("No docId");
            return;
        }

        const content = editorRef.current.innerHTML;

        const payload = {
            document_id: docId,
            author_id: user.id,
            content: content,
            anchor_type: pendingAnchor ? 'text_highlight' : 'document',
        };
        if (pendingAnchor) {
            payload.metadata = { anchorId: pendingAnchor.tempId };
        }

        try {
            const data = await createComment.mutateAsync(payload);

            if (pendingAnchor) {
                setRealizedAnchor({ tempId: pendingAnchor.tempId, realId: data.comment.comment_id });
                clearPendingAnchor();
            }

            editorRef.current.innerHTML = '';
            setSmartTags([]);
        } catch (e) {
            console.error("Post error", e);
            toast.error("No se pudo guardar el comentario: " + e.message);
        }
    };

    const cancelPendingAnchor = () => {
        if (!pendingAnchor) return;
        setCancelledAnchorId(pendingAnchor.tempId);
        clearPendingAnchor();
    };

    // Drawing Logic
    const startDraw = (e) => {
        if (!isDrawing) return;
        const ctx = canvasRef.current.getContext('2d');
        const rect = canvasRef.current.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
        setIsDrawingMouse(true);
    };

    const draw = (e) => {
        if (!isDrawing || !isDrawingMouse) return;
        const ctx = canvasRef.current.getContext('2d');
        const rect = canvasRef.current.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.strokeStyle = '#ef4444';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
    };

    const stopDraw = () => {
        if (isDrawingMouse) {
            setIsDrawingMouse(false);
            // Optionally convert canvas to image and insert into editor?
            // For now, it's an overlay. Saving it requires logic user hasn't specified fully.
        }
    };

    useEffect(() => {
        if (isDrawing && canvasRef.current && editorRef.current) {
            canvasRef.current.width = editorRef.current.offsetWidth;
            canvasRef.current.height = editorRef.current.offsetHeight;
        }
    }, [isDrawing]);

    return (
        <div className="comment-area-container">
            {pendingAnchor && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '6px 10px', margin: '6px 10px 0', borderRadius: '6px',
                    backgroundColor: '#1e293b', border: '1px solid #334155',
                    fontSize: '12px', color: '#94a3b8',
                }}>
                    <MessageCircle size={12} color="#60a5fa" style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        Comentando sobre: “{pendingAnchor.previewText}”
                    </span>
                    <X size={14} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={cancelPendingAnchor} />
                </div>
            )}
            <div className="smart-tags-bar">
                {smartTags.map(tag => (
                    <span key={tag} className="smart-tag">{tag}</span>
                ))}
            </div>

            <div className="editor-wrapper">
                <div
                    ref={editorRef}
                    className={`rich-editor ${isDragOver ? 'drag-over' : ''}`}
                    contentEditable={!isDrawing}
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onKeyDown={handleKeyDown}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    placeholder="Escribe un comentario o @menciona..."
                    suppressContentEditableWarning={true}
                />

                {/* Mention Suggestions (Portal) */}
                {mentionQuery !== null && filteredUsers.length > 0 && (
                    <CommentMentionSuggestions
                        filteredUsers={filteredUsers}
                        mentionIndex={mentionIndex}
                        coords={dropdownCoords}
                        onSelect={insertMention}
                    />
                )}

                {isDrawing && (
                    <canvas
                        ref={canvasRef}
                        className="drawing-canvas-layer"
                        onMouseDown={startDraw}
                        onMouseMove={draw}
                        onMouseUp={stopDraw}
                        onMouseLeave={stopDraw}
                    />
                )}
            </div>

            <CommentToolbar
                isRecording={isRecordingFromComment}
                onToggleRecording={handleToggleRecording}
                onFilesSelected={uploadFile}
                onSend={submitComment}
            />
            {uploadProgress > 0 && (
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '3px',
                    width: `${uploadProgress}%`,
                    backgroundColor: '#10b981',
                    borderRadius: '0 3px 3px 0',
                    transition: 'width 0.1s linear',
                    zIndex: 20
                }} />
            )}
        </div>
    );
};

export default CommentArea;
